import {
  Button,
  SkeletonLoader,
  SkeletonLoaderArea,
} from '@toggle/design-system';
import { SingleNews } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Markdown } from '~/components/markdown/Markdown';

import { ArticleDate } from '../article-date/ArticleDate';
import { AssetChip } from '../asset-chip/AssetChip';
import styles from './SingleArticle.module.scss';

export interface SingleArticleProps {
  article: SingleNews;
  isLoading: boolean;
  onEntityClick(entityTag: string): void;
}

export const contentSkeletonAreas: SkeletonLoaderArea[] = [
  {
    styles: {
      width: '100%',
      height: '16px',
      borderRadius: '8px',
      marginBottom: '8px',
    },
  },
  {
    styles: {
      width: '100%',
      height: '16px',
      borderRadius: '8px',
      marginBottom: '8px',
    },
  },
  {
    styles: {
      width: '100%',
      height: '16px',
      borderRadius: '8px',
      marginBottom: '8px',
    },
  },
  {
    styles: {
      width: '80%',
      height: '16px',
      borderRadius: '8px',
      marginBottom: '8px',
    },
  },
];

const ENTITY_DISPLAY_LIMIT = 5;

export const SingleArticle = ({
  article,
  isLoading,
  onEntityClick,
}: SingleArticleProps) => {
  const { t } = useTranslation('news');
  const [showMore, setShowMore] = useState(false);

  const handleMore = () => {
    setShowMore(true);
  };

  const entities = showMore
    ? article.entities
    : article.entities.slice(0, ENTITY_DISPLAY_LIMIT);

  return (
    <article className={styles.singleArticleRoot} data-testid="single-article">
      <h1 className={styles.title}>{article.title}</h1>

      <section className={styles.meta}>
        <span className={styles.metaItem}>
          <ArticleDate date={article.published} />
        </span>
        <span className={styles.metaItem}>
          {
            /* i18next-extract-mark-context-next-line ["benzinga", "mtn", "dowjones"] */
            t('news:source', {
              context: article.source,
            })
          }
        </span>
      </section>

      <div className={styles.assetChips}>
        {entities.map(entityTag => (
          <AssetChip
            key={entityTag}
            entityTag={entityTag}
            onEntityClick={onEntityClick}
          />
        ))}
        {article.entities.length > ENTITY_DISPLAY_LIMIT && !showMore && (
          <Button
            className={styles.viewMoreButton}
            variant="secondary"
            onClick={handleMore}
            size="small"
          >
            {t('news:seeMore')}
          </Button>
        )}
      </div>

      {isLoading && (
        <div
          className={styles.contentSkeletonWrapper}
          data-testid="loading-state"
        >
          <SkeletonLoader areas={contentSkeletonAreas} />
        </div>
      )}

      {!isLoading && article?.body && <Markdown message={article.body} />}
    </article>
  );
};
